import { useState, useEffect, useMemo } from "react";
import { Landmark, Printer, ChevronLeft, ChevronRight, User, Building2, HelpCircle, Receipt } from "lucide-react";
import { subscribeDocuments, COMPANY } from "@/lib/documentsApi";
import { partiesApi, financeApi } from "@/lib/unifiedApi";
import { taxParts, normalizeRows } from "@/lib/financeUtils";
import { useAuth } from "@/context/AuthContext";

const fmt = (n) => Number(n || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (mk) => {
  const [y, m] = mk.split("-").map(Number);
  const TH = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
  return `${TH[m - 1]} ${y + 543}`;
};

// ─── Print a ภ.ง.ด. filing list ─────────────────────────────
function printPND(rows, pndType, mk) {
  const total = rows.reduce((s, r) => s + r.wht, 0);
  const totalBase = rows.reduce((s, r) => s + r.base, 0);
  const body = rows.map((r, i) => `
    <tr>
      <td class="c">${i + 1}</td>
      <td>${r.name || "-"}<div class="sub">${r.tax_id || ""}</div></td>
      <td class="c">${r.date || "-"}</td>
      <td class="r">${fmt(r.base)}</td>
      <td class="c">${r.rate}%</td>
      <td class="r">${fmt(r.wht)}</td>
    </tr>`).join("");
  const html = `<!doctype html><html lang="th"><head><meta charset="utf-8">
    <title>${pndType} ${mk}</title>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;600;700;800&display=swap');
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:'Sarabun',sans-serif;color:#111;padding:28px 32px;font-size:13px}
      h1{font-size:18px;font-weight:800}
      .meta{color:#444;margin:2px 0 14px;font-size:12px}
      table{width:100%;border-collapse:collapse;margin-top:8px}
      th{background:#334155;color:#fff;padding:7px 8px;font-size:11px;text-align:left}
      th.r,td.r{text-align:right}th.c,td.c{text-align:center}
      td{border-bottom:1px solid #e2e8f0;padding:7px 8px;font-size:12px;vertical-align:top}
      .sub{font-size:10px;color:#888}
      tfoot td{font-weight:800;border-top:2px solid #334155;background:#f1f5f9}
      .btn{display:block;margin:20px 0 0 auto;padding:8px 22px;background:#2563eb;color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit}
      @media print{.btn{display:none}body{padding:0}th,tfoot td{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
      @page{margin:14mm;size:A4}
    </style></head><body>
    <h1>${pndType} — รายการภาษีหัก ณ ที่จ่าย</h1>
    <div class="meta">${COMPANY.name} · เลขประจำตัวผู้เสียภาษี ${COMPANY.tax_id}<br>ประจำเดือน ${monthLabel(mk)}</div>
    <table>
      <thead><tr>
        <th class="c" style="width:32px">#</th>
        <th>ผู้ถูกหักภาษี</th>
        <th class="c" style="width:90px">วันที่จ่าย</th>
        <th class="r" style="width:120px">ยอดเงินที่จ่าย</th>
        <th class="c" style="width:60px">อัตรา</th>
        <th class="r" style="width:120px">ภาษีที่หัก</th>
      </tr></thead>
      <tbody>${body || '<tr><td colspan="6" class="c" style="padding:16px;color:#aaa">ไม่มีรายการ</td></tr>'}</tbody>
      <tfoot><tr>
        <td colspan="3" class="r">รวม (${rows.length} ราย)</td>
        <td class="r">${fmt(totalBase)}</td><td></td><td class="r">${fmt(total)}</td>
      </tr></tfoot>
    </table>
    <button class="btn" onclick="window.print()">พิมพ์ / บันทึก PDF</button>
  </body></html>`;
  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
}

// ─── WHT table ──────────────────────────────────────────────
function WhtTable({ title, subtitle, icon: Icon, rows, onPrint }) {
  const total = rows.reduce((s, r) => s + r.wht, 0);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-blue-600" />
          <div>
            <div className="font-semibold text-slate-800 text-sm">{title}</div>
            <div className="text-xs text-slate-400">{subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] text-slate-400">รวมภาษีหัก</div>
            <div className="font-bold text-slate-800">฿{fmt(total)}</div>
          </div>
          <button onClick={onPrint} disabled={rows.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
            <Printer size={13} /> พิมพ์
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs">
              <th className="text-left px-4 py-2 font-medium">ผู้ถูกหัก</th>
              <th className="text-left px-4 py-2 font-medium">เอกสาร</th>
              <th className="text-left px-4 py-2 font-medium">วันที่</th>
              <th className="text-right px-4 py-2 font-medium">ยอดจ่าย</th>
              <th className="text-center px-4 py-2 font-medium">อัตรา</th>
              <th className="text-right px-4 py-2 font-medium">ภาษีหัก</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400 text-sm">ไม่มีรายการในเดือนนี้</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 last:border-0">
                <td className="px-4 py-2 text-slate-700">
                  {r.name || "-"}
                  {r.tax_id && <span className="text-slate-400 text-xs ml-1">· {r.tax_id}</span>}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-blue-600">{r.doc_number}</td>
                <td className="px-4 py-2 text-slate-400 text-xs">{r.date || "-"}</td>
                <td className="px-4 py-2 text-right text-slate-700 tabular-nums">฿{fmt(r.base)}</td>
                <td className="px-4 py-2 text-center text-slate-500">{r.rate}%</td>
                <td className="px-4 py-2 text-right text-slate-800 font-medium tabular-nums">฿{fmt(r.wht)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────
export default function TaxReportsPage() {
  const { isAdmin } = useAuth();
  const [docs, setDocs] = useState([]);
  const [parties, setParties] = useState([]);
  const [financeRows, setFinanceRows] = useState([]);
  const [month, setMonth] = useState(monthKey(new Date()));

  useEffect(() => {
    const unsubDocs = subscribeDocuments((d) => setDocs(d));
    const unsubParties = partiesApi.subscribe((p) => setParties(p), () => {});
    financeApi.load().then((f) => setFinanceRows(normalizeRows(f?.rows || []))).catch(() => {});
    return () => { unsubDocs(); unsubParties(); };
  }, []);

  // ── VAT (ภ.พ.30) ──
  // Output VAT: tax invoices (TI) we issued this month (legal source).
  const tiDocs = useMemo(() =>
    docs.filter((d) => d.doc_type === "TI" && (d.issue_date || "").startsWith(month) && d.status !== "void"),
    [docs, month]);
  const outputVat = tiDocs.reduce((s, d) => s + Number(d.vat_amount || 0), 0);
  const outputBase = tiDocs.reduce((s, d) => s + Number(d.subtotal || 0), 0);
  // Input VAT: purchase rows with VAT from the Finance ledger this month.
  const inputRows = useMemo(() =>
    financeRows.filter((r) => r.monthKey === month && Number(r.expense || 0) > 0 && r.hasVat),
    [financeRows, month]);
  const inputVat = inputRows.reduce((s, r) => s + taxParts(r).vat, 0);
  const netVat = outputVat - inputVat;

  const partyMap = useMemo(() => {
    const m = {};
    parties.forEach((p) => { m[p.id] = p; });
    return m;
  }, [parties]);

  // WHT rows for the month, from WT/WC documents
  const whtRows = useMemo(() => {
    return docs
      .filter((d) => ["WT", "WC"].includes(d.doc_type) && (d.issue_date || "").startsWith(month) && Number(d.wht_amount || 0) > 0)
      .map((d) => {
        const party = d.party_id ? partyMap[d.party_id] : null;
        const kind = party?.is_company === "individual" ? "pnd3"
          : party?.is_company === "company" ? "pnd53"
          : "unknown";
        return {
          id: d.id,
          doc_number: d.doc_number,
          name: d.customer?.name || party?.name || "-",
          tax_id: d.customer?.tax_id || party?.tax_id || "",
          date: d.issue_date,
          base: Number(d.subtotal || 0),
          rate: Number(d.wht_rate || 0),
          wht: Number(d.wht_amount || 0),
          kind,
        };
      });
  }, [docs, month, partyMap]);

  const pnd3 = whtRows.filter((r) => r.kind === "pnd3");
  const pnd53 = whtRows.filter((r) => r.kind === "pnd53");
  const unknown = whtRows.filter((r) => r.kind === "unknown");
  const totalWht = whtRows.reduce((s, r) => s + r.wht, 0);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(monthKey(d));
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <p className="text-slate-400">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 p-5 md:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Landmark size={24} className="text-blue-600" />รายงานภาษี
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">VAT (ภ.พ.30) และภาษีหัก ณ ที่จ่าย (ภ.ง.ด.3/53) — สำหรับเตรียมยื่น</p>
        </div>
        {/* Month selector */}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl px-2 py-1 shadow-sm">
          <button onClick={() => shiftMonth(-1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><ChevronLeft size={16} /></button>
          <span className="px-2 text-sm font-semibold text-slate-700 min-w-[90px] text-center">{monthLabel(month)}</span>
          <button onClick={() => shiftMonth(1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><ChevronRight size={16} /></button>
        </div>
      </div>

      {/* VAT (ภ.พ.30) */}
      <div>
        <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5 mb-2">
          <Receipt size={15} className="text-blue-600" />ภาษีมูลค่าเพิ่ม (ภ.พ.30)
        </h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
            <div className="text-xs text-slate-400">VAT ขาย (จากใบกำกับ TI)</div>
            <div className="text-2xl font-bold text-slate-800 mt-1">฿{fmt(outputVat)}</div>
            <div className="text-xs text-slate-400 mt-0.5">ฐาน ฿{fmt(outputBase)} · {tiDocs.length} ใบ</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
            <div className="text-xs text-slate-400">VAT ซื้อ (จาก Finance)</div>
            <div className="text-2xl font-bold text-slate-800 mt-1">฿{fmt(inputVat)}</div>
            <div className="text-xs text-slate-400 mt-0.5">{inputRows.length} รายการ</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
            <div className="text-xs text-slate-400">{netVat >= 0 ? "VAT ต้องนำส่งสุทธิ" : "VAT ขอคืน/ยกไป"}</div>
            <div className={`text-2xl font-bold mt-1 ${netVat >= 0 ? "text-blue-600" : "text-emerald-600"}`}>฿{fmt(Math.abs(netVat))}</div>
            <div className="text-xs text-slate-400 mt-0.5">ขาย − ซื้อ</div>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          * VAT ขาย นับเฉพาะยอดที่ออกใบกำกับภาษี (TI) ใน Billing · VAT ซื้อ ดึงจากรายการมี VAT ในหน้า Finance เดือนนั้น
        </p>
      </div>

      {/* WHT (ภ.ง.ด.3/53) */}
      <h2 className="text-sm font-bold text-slate-700 flex items-center gap-1.5 pt-1">
        <Landmark size={15} className="text-blue-600" />ภาษีหัก ณ ที่จ่าย (ภ.ง.ด.3 / ภ.ง.ด.53)
      </h2>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
          <div className="text-xs text-slate-400">ภ.ง.ด.3 (บุคคลธรรมดา)</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">฿{fmt(pnd3.reduce((s, r) => s + r.wht, 0))}</div>
          <div className="text-xs text-slate-400 mt-0.5">{pnd3.length} ราย</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
          <div className="text-xs text-slate-400">ภ.ง.ด.53 (นิติบุคคล)</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">฿{fmt(pnd53.reduce((s, r) => s + r.wht, 0))}</div>
          <div className="text-xs text-slate-400 mt-0.5">{pnd53.length} ราย</div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
          <div className="text-xs text-slate-400">รวมนำส่งทั้งสิ้น</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">฿{fmt(totalWht)}</div>
          <div className="text-xs text-slate-400 mt-0.5">{whtRows.length} ราย</div>
        </div>
      </div>

      <WhtTable title="ภ.ง.ด.3" subtitle="ผู้ถูกหักเป็นบุคคลธรรมดา" icon={User}
        rows={pnd3} onPrint={() => printPND(pnd3, "ภ.ง.ด.3", month)} />

      <WhtTable title="ภ.ง.ด.53" subtitle="ผู้ถูกหักเป็นนิติบุคคล" icon={Building2}
        rows={pnd53} onPrint={() => printPND(pnd53, "ภ.ง.ด.53", month)} />

      {unknown.length > 0 && (
        <WhtTable title="ยังไม่ระบุประเภท" subtitle="เอกสารที่ยังไม่ผูกคู่ค้า — ผูกที่ Billing เพื่อจัดกลุ่ม ภ.ง.ด.3/53"
          icon={HelpCircle} rows={unknown} onPrint={() => printPND(unknown, "ยังไม่ระบุ", month)} />
      )}

      <p className="text-xs text-slate-400">
        * ดึงจากเอกสาร ใบหัก ณ ที่จ่าย (WT) และหนังสือรับรองฯ 50 ทวิ (WC) ใน Billing ·
        แยก ภ.ง.ด.3/53 ตามประเภทผู้เสียภาษีของคู่ค้า · รายงานภายในเพื่อเตรียมยื่น ไม่ใช่การยื่นจริง
      </p>
    </div>
  );
}
